import { createApi } from '@reduxjs/toolkit/query/react';

import { setError } from '@/apps/common/settings/redux/configurationSlice';
import { IWashRuleConfig } from '@/apps/common/settings/types/ISettings';
import { axiosBaseQuery } from '@/redux/axios';
import { apiRoutes } from '@/api/apiRoutes';
import { getRtkErrorStatus } from '@/redux/rtkQueryErrors';
import {
  createNotificationHandler,
  SCOPES,
  MESSAGES,
} from '@/redux/notificationHandler';

const notifyLoadFailure = createNotificationHandler(
  setError,
  SCOPES.SETTINGS_WASH_RULES,
  MESSAGES.LOAD,
  false
);

/**
 * A 403 here is a state, not a fault: the caller may see the washing settings
 * but holds no contract with a monitored depot. The page renders a dedicated
 * empty state for it, so neither a toast nor the sticky `setError` banner
 * belongs — every other failure keeps reporting exactly as before.
 */
const notifyLoadFailureExceptForbidden = async (
  args: void,
  lifecycle: Parameters<typeof notifyLoadFailure>[1]
) => {
  try {
    await lifecycle.queryFulfilled;
  } catch (caught) {
    // queryFulfilled rejects with { error, isUnhandledError, meta }; the
    // normalized { status, data } from axiosBaseQuery is one level in.
    const inner = (caught as { error?: unknown }).error;
    if (getRtkErrorStatus(inner) === 403) {
      return;
    }
  }
  await notifyLoadFailure(args, lifecycle);
};

export const washRuleConfigurationsApi = createApi({
  reducerPath: 'washRuleConfigurationsApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['WashRuleConfigurations'],
  endpoints: (builder) => ({
    getWashRuleConfigurations: builder.query<IWashRuleConfig[], void>({
      query: () => ({
        url: apiRoutes.getWashRuleConfigurations.path(),
        method: apiRoutes.getWashRuleConfigurations.method,
      }),
      transformResponse: (response: {
        ruleConfigurations: IWashRuleConfig[];
      }) => response.ruleConfigurations,
      providesTags: ['WashRuleConfigurations'],
      onQueryStarted: notifyLoadFailureExceptForbidden,
    }),
    updateWashRuleConfiguration: builder.mutation<
      IWashRuleConfig,
      Pick<
        IWashRuleConfig,
        | 'id'
        | 'daysRuleEnabled'
        | 'daysThreshold'
        | 'kmRuleEnabled'
        | 'kmThreshold'
      >
    >({
      query: ({ id, ...body }) => ({
        url: apiRoutes.updateWashRuleConfigurationById.path(id as number),
        method: apiRoutes.updateWashRuleConfigurationById.method,
        data: body,
      }),
      async onQueryStarted({ id, ...body }, { dispatch, queryFulfilled }) {
        createNotificationHandler(
          setError,
          SCOPES.SETTINGS_WASH_RULES,
          MESSAGES.UPDATE
        )({ id, ...body }, { dispatch, queryFulfilled });

        try {
          const { data: updatedItem } = await queryFulfilled;

          dispatch(
            washRuleConfigurationsApi.util.updateQueryData(
              'getWashRuleConfigurations',
              undefined,
              (draft) => {
                const idx = draft.findIndex(
                  (item) => item.id === updatedItem.id
                );
                if (idx !== -1) {
                  draft[idx] = updatedItem;
                }
              }
            )
          );
        } catch {
          // Failure is already reported by the notification handler above.
        }
      },
    }),
  }),
});

export const {
  useGetWashRuleConfigurationsQuery,
  useUpdateWashRuleConfigurationMutation,
} = washRuleConfigurationsApi;

export default washRuleConfigurationsApi;
